import { Injectable } from '@angular/core';
import { ActionNeededDto, ActionNeededType } from '../../../models/common/ActionNeededDto';
import {
  CostReviewAttributes, CostReviewDatabaseRecord, CostReviewElectricalAttributes, CostReviewFireEnUsAttributes, CostReviewMarginExceptions,
  CostReviewPrefab, CostReviewSecurityEnUsAttributes, CostReviewSummary
} from '../../../models/dtos';
import { ActionNeededField, ActionNeededFieldChange } from 'review-detail-tabs';
import { CONSTANTS } from '../../../Constants/Constants';
import { ReviewDetailComponent } from '../../../Components/Common/review-detail/review-detail.component';

@Injectable({ providedIn: ReviewDetailComponent })
export class ActionNeededMappingService {

  // ===========================================================================
  //  Automation / Electrical / Mechanical
  // ===========================================================================

  applyAllAutomationActionNeeded(
    collection: ActionNeededDto[],
    costReviewAttributes: CostReviewAttributes
  ): void {
    if (!collection || !costReviewAttributes) {
      return;
    }

    const marginExceptions: CostReviewMarginExceptions = costReviewAttributes.costReviewMarginExceptions;
    const prefab: CostReviewPrefab = costReviewAttributes.costReviewPrefab;

    for (const item of collection) {
      this.applyAutomationTextValues(item, costReviewAttributes, marginExceptions, prefab);
      this.applyAutomationApprovalValues(item, costReviewAttributes, marginExceptions);
    }
  }

  applySingleAutomationFieldChange(
    change: ActionNeededFieldChange,
    target: CostReviewDatabaseRecord
  ): void {
    const costReviewAttributes = target?.reviewObject as CostReviewAttributes;
    if (!change || !costReviewAttributes) {
      return;
    }

    const attrs: CostReviewAttributes = costReviewAttributes;
    const marginExceptions: CostReviewMarginExceptions = attrs.costReviewMarginExceptions;
    const prefab: CostReviewPrefab = attrs.costReviewPrefab;

    switch (change.field) {
      // ── CostReviewMarginExceptions ─────────────────────────────────────
      case ActionNeededField.MarginReasonForException:
        if (marginExceptions) { marginExceptions.reasonForException = change.value as string; }
        break;
      case ActionNeededField.MarginApproverEmailId:
        if (marginExceptions) { marginExceptions.approverEmailId = change.value as string; }
        break;
      case ActionNeededField.MarginSmOrBgmApprovalReceived:
        if (marginExceptions) { marginExceptions.smOrBgmApprovalRecieved = change.value as number; }
        break;
      case ActionNeededField.MarginSmApprovalReceived:
        if (marginExceptions) { marginExceptions.smApprovalReceived = change.value as number; }
        break;
      case ActionNeededField.MarginIsReasonForExceptionApplicable:
        if (marginExceptions) { marginExceptions.isReasonForExceptionApplicable = change.value as boolean; }
        break;
      case ActionNeededField.MarginIsBGMApprovalApplicable:
        if (marginExceptions) { marginExceptions.isBGMApprovalApplicable = change.value as boolean; }
        break;

      // ── CostReviewBaseAttributes ───────────────────────────────────────
      case ActionNeededField.IsEngineerExceptionApplicable:
        attrs.isEngineerExceptionApplicable = change.value as boolean;
        break;
      case ActionNeededField.EngineerApprovalReceived:
        attrs.engineerApprovalReceived = change.value as number;
        break;
      case ActionNeededField.EngineerApproverEmail:
        attrs.engineerApproverEmail = change.value as string;
        break;
      case ActionNeededField.EngineerReasonForException:
        attrs.engineerReasonForException = change.value as string;
        break;

      // ── CostReviewPrefab ──────────────────────────────────────────────
      case ActionNeededField.PrefabReasonForException:
        if (prefab) { prefab.reasonForException = change.value as string; }
        break;
      case ActionNeededField.PrefabIsPrefabControllerVisible:
        if (prefab) { prefab.isPrefabControllerVisible = change.value as boolean; }
        break;
      case ActionNeededField.PrefabIsReasonForExceptionVisible:
        if (prefab) { prefab.isReasonForExceptionVisible = change.value as boolean; }
        break;

      default:
        break;
    }
  }

  private applyAutomationTextValues(
    item: ActionNeededDto,
    costReviewAttributes: CostReviewAttributes,
    marginExceptions: CostReviewMarginExceptions,
    prefab: CostReviewPrefab
  ): void {
    if (item.isAutomationComboboxVisible || item.isElectricalComboboxVisible || item.isMechanicalComboboxVisible) {
      marginExceptions.reasonForException = item.actionTextValue ?? '';
    }

    if (item.isApproverEmailTextboxVisible) {
      marginExceptions.approverEmailId = item.actionTextValue ?? '';
    }

    if (item.isEngineerApproverEmailTextboxVisible) {
      costReviewAttributes.engineerApproverEmail = item.actionTextValue ?? '';
    }

    if (item.isEngineerReasonForExceptionComboboxVisible) {
      costReviewAttributes.engineerReasonForException = item.actionTextValue ?? '';
    }

    if (item.isPrefabReasonForExceptionComboboxVisible && prefab) {
      prefab.reasonForException = item.actionTextValue ?? '';
    }
  }

  private applyAutomationApprovalValues(
    item: ActionNeededDto,
    costReviewAttributes: CostReviewAttributes,
    marginExceptions: CostReviewMarginExceptions
  ): void {
    if (item.isAction) {
      switch (item.type) {
        case ActionNeededType.MarginSmBgmApproval:
          marginExceptions.smOrBgmApprovalRecieved = CONSTANTS.ONE;
          break;
        case ActionNeededType.EscalationSmBgmApproval:
          marginExceptions.smApprovalReceived = CONSTANTS.ONE;
          break;
        default:
          break;
      }
    }

    if (item.isEngineerApproval && item.type === ActionNeededType.EngineerApproval) {
      costReviewAttributes.engineerApprovalReceived = CONSTANTS.ONE;
    }
  }

  // ===========================================================================
  //  Electrical
  // ===========================================================================

  /**
   * Electrical only ever carries the four Margin exception items, so unlike
   * Automation it never writes Engineer or Prefab values.
   */
  applyAllElectricalActionNeeded(
    collection: ActionNeededDto[],
    electricalAttributes: CostReviewElectricalAttributes
  ): void {
    if (!collection || !electricalAttributes) {
      return;
    }

    const marginExceptions: CostReviewMarginExceptions = electricalAttributes.costReviewMarginExceptions;
    if (!marginExceptions) {
      return;
    }

    for (const item of collection) {
      if (item.isElectricalComboboxVisible) {
        marginExceptions.reasonForException = item.actionTextValue ?? '';
      }

      if (item.isApproverEmailTextboxVisible) {
        marginExceptions.approverEmailId = item.actionTextValue ?? '';
      }

      if (item.isAction) {
        switch (item.type) {
          case ActionNeededType.MarginSmBgmApproval:
            marginExceptions.smOrBgmApprovalRecieved = CONSTANTS.ONE;
            break;
          case ActionNeededType.EscalationSmBgmApproval:
            marginExceptions.smApprovalReceived = CONSTANTS.ONE;
            break;
          default:
            break;
        }
      }
    }
  }

  // ===========================================================================
  //  Fire
  // ===========================================================================

  applyAllFireActionNeeded(
    collection: ActionNeededDto[],
    fireAttributes: CostReviewFireEnUsAttributes
  ): void {
    if (!collection || !fireAttributes) {
      return;
    }

    const marginExceptions: CostReviewMarginExceptions = fireAttributes.costReviewMarginExceptions;
    const costReviewSummary: CostReviewSummary = fireAttributes.costReviewSummary;

    for (const item of collection) {
      this.applyFireTextValues(item, marginExceptions);
      this.applyFireApprovalValues(item, marginExceptions, costReviewSummary);
    }
  }

  applySingleFireFieldChange(
    change: ActionNeededFieldChange,
    target: CostReviewDatabaseRecord
  ): void {
    const costReviewFireAttributes = target?.reviewObject as CostReviewFireEnUsAttributes;
    if (!change || !costReviewFireAttributes) {
      return;
    }

    const fireAttrs: CostReviewFireEnUsAttributes = costReviewFireAttributes;

    this.applyFireMarginFieldChange(change, fireAttrs.costReviewMarginExceptions);
    this.applyFireSummaryFieldChange(change, fireAttrs.costReviewSummary);
  }

  private applyFireTextValues(
    item: ActionNeededDto,
    marginExceptions: CostReviewMarginExceptions
  ): void {
    if (item.isFireComboboxVisible) {
      marginExceptions.reasonForException = item.actionTextValue ?? '';
    }

    if (item.isApproverEmailTextboxVisible) {
      marginExceptions.approverEmailId = item.actionTextValue ?? '';
    }
  }

  private applyFireApprovalValues(
    item: ActionNeededDto,
    marginExceptions: CostReviewMarginExceptions,
    costReviewSummary: CostReviewSummary
  ): void {
    if (item.isAction) {
      switch (item.type) {
        case ActionNeededType.FireZteApproval:
          costReviewSummary.zteApprovalReceived = CONSTANTS.ONE;
          break;
        case ActionNeededType.MarginSmBgmApproval:
          marginExceptions.smOrBgmApprovalRecieved = CONSTANTS.ONE;
          break;
        case ActionNeededType.EscalationSmBgmApproval:
          marginExceptions.smApprovalReceived = CONSTANTS.ONE;
          break;
        default:
          break;
      }
    }

    if (item.isSMApproval && item.type === ActionNeededType.FireTbeSmApproval) {
      costReviewSummary.smApprovalRecieved = CONSTANTS.ONE;
      costReviewSummary.isTBEApprovalTaken = true;
    }
  }

  private applyFireMarginFieldChange(
    change: ActionNeededFieldChange,
    marginExceptions: CostReviewMarginExceptions
  ): void {
    if (!marginExceptions) {
      return;
    }

    switch (change.field) {
      case ActionNeededField.FireMarginReasonForException:
        marginExceptions.reasonForException = change.value as string;
        break;
      case ActionNeededField.FireMarginApproverEmailId:
        marginExceptions.approverEmailId = change.value as string;
        break;
      case ActionNeededField.FireMarginSmOrBgmApprovalReceived:
        marginExceptions.smOrBgmApprovalRecieved = change.value as number;
        break;
      case ActionNeededField.FireMarginSmApprovalReceived:
        marginExceptions.smApprovalReceived = change.value as number;
        break;
      case ActionNeededField.FireMarginIsReasonForExceptionApplicable:
        marginExceptions.isReasonForExceptionApplicable = change.value as boolean;
        break;
      case ActionNeededField.FireMarginIsBGMApprovalApplicable:
        marginExceptions.isBGMApprovalApplicable = change.value as boolean;
        break;
      default:
        break;
    }
  }

  private applyFireSummaryFieldChange(
    change: ActionNeededFieldChange,
    summary: CostReviewSummary
  ): void {
    if (!summary) {
      return;
    }

    switch (change.field) {
      case ActionNeededField.FireIsZteApprovalVisible:
        summary.isZteApprovalVisible = change.value as boolean;
        break;
      case ActionNeededField.FireZteApprovalReceived:
        summary.zteApprovalReceived = change.value as number;
        break;
      case ActionNeededField.FireSmApprovalReceived:
        summary.smApprovalRecieved = change.value as number;
        break;
      default:
        break;
    }
  }

  // ===========================================================================
  //  Security
  // ===========================================================================

  applyAllSecurityActionNeeded(
    collection: ActionNeededDto[],
    securityAttributes: CostReviewSecurityEnUsAttributes
  ): void {
    if (!collection || !securityAttributes) {
      return;
    }

    const marginExceptions: CostReviewMarginExceptions = securityAttributes.costReviewMarginExceptions;

    for (const item of collection) {
      this.applySecurityTextValues(item, securityAttributes, marginExceptions);
      this.applySecurityApprovalValues(item, marginExceptions);
    }
  }

  applySingleSecurityFieldChange(
    change: ActionNeededFieldChange,
    target: CostReviewDatabaseRecord
  ): void {
    const costReviewSecurityAttributes = target?.reviewObject as CostReviewSecurityEnUsAttributes;
    if (!change || !costReviewSecurityAttributes) {
      return;
    }

    const securityAttrs: CostReviewSecurityEnUsAttributes = costReviewSecurityAttributes;
    const marginExceptions: CostReviewMarginExceptions = securityAttrs.costReviewMarginExceptions;

    switch (change.field) {
      // ── CostReviewMarginExceptions (Security) ─────────────────────────
      case ActionNeededField.SecurityMarginReasonForException:
        if (marginExceptions) { marginExceptions.reasonForException = change.value as string; }
        break;
      case ActionNeededField.SecurityMarginApproverEmailId:
        if (marginExceptions) { marginExceptions.approverEmailId = change.value as string; }
        break;
      case ActionNeededField.SecurityMarginSmOrBgmApprovalReceived:
        if (marginExceptions) { marginExceptions.smOrBgmApprovalRecieved = change.value as number; }
        break;
      case ActionNeededField.SecurityMarginSmApprovalReceived:
        if (marginExceptions) { marginExceptions.smApprovalReceived = change.value as number; }
        break;
      case ActionNeededField.SecurityMarginIsReasonForExceptionApplicable:
        if (marginExceptions) { marginExceptions.isReasonForExceptionApplicable = change.value as boolean; }
        break;
      case ActionNeededField.SecurityMarginIsBGMApprovalApplicable:
        if (marginExceptions) { marginExceptions.isBGMApprovalApplicable = change.value as boolean; }
        break;

      // ── CostReviewSecurityEnUsAttributes ──────────────────────────────
      case ActionNeededField.SecurityIsLaborTDCPercentExceeds:
        securityAttrs.isLaborTDCPercentExceeds = change.value as boolean;
        break;
      case ActionNeededField.SecurityReasonForLaborVariance:
        securityAttrs.reasonForLaborVariance = change.value as string;
        break;

      default:
        break;
    }
  }

  private applySecurityTextValues(
    item: ActionNeededDto,
    securityAttributes: CostReviewSecurityEnUsAttributes,
    marginExceptions: CostReviewMarginExceptions
  ): void {
    if (item.isSecurityComboboxVisible) {
      marginExceptions.reasonForException = item.actionTextValue ?? '';
    }

    if (item.isApproverEmailTextboxVisible) {
      marginExceptions.approverEmailId = item.actionTextValue ?? '';
    }

    if (item.isSecurityReasonForLaborVarianceComboboxVisible) {
      securityAttributes.reasonForLaborVariance = item.actionTextValue ?? '';
    }
  }

  private applySecurityApprovalValues(
    item: ActionNeededDto,
    marginExceptions: CostReviewMarginExceptions
  ): void {
    if (item.isAction) {
      switch (item.type) {
        case ActionNeededType.SecurityMarginSmBgmApproval:
          marginExceptions.smOrBgmApprovalRecieved = CONSTANTS.ONE;
          break;
        case ActionNeededType.SecurityEscalationSmBgmApproval:
          marginExceptions.smApprovalReceived = CONSTANTS.ONE;
          break;
        default:
          break;
      }
    }
  }
}
